import { useEffect, useMemo, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";

import { Column, Row } from "@/components";
import { FormDivider } from "@/components/form/FormDivider";
import { FormSection } from "@/components/form/FormSection";
import { JobSignupPreview } from "@/components/tools/JobSignupPreview";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

import { useGetTargetJob } from "@/hooks/queries/useGetTargetJob";
import { useGetCachedCompaniesSummary } from "@/hooks/sets/useGetCachedCompaniesSummary";
import {
  compensationTypeOptions,
  desiredPositionOptions,
} from "@/lib/constants";
import { misc } from "@/lib/misc";

import {
  CompensationType,
  CreateOrUpdateSignupVariantRequest,
  GenericSignupVariantPayload,
  JobSpecificSignupVariantPayload,
  JobStatus,
  ScreeningQuestionType,
  ShiftType,
  SignupVariant,
  SignupVariantBadge,
  SignupVariantPayload,
} from "@/types";

const BADGE_OPTIONS = [
  { value: SignupVariantBadge.HIRING_MULTIPLE_ROLES, label: "Hiring Multiple Roles" },
  { value: SignupVariantBadge.FULL_MEDICAL_DENTAL_VISION, label: "Full Medical, Dental, Vision" },
  { value: SignupVariantBadge.FAST_GROWING_COMPANY, label: "Fast Growing Company" },
];

const SHIFT_OPTIONS = [
  { value: ShiftType.FIRSTSHIFT, label: "1st shift" },
  { value: ShiftType.SECONDSHIFT, label: "2nd shift" },
  { value: ShiftType.THIRDSHIFT, label: "3rd shift" },
  { value: ShiftType.WEEKEND, label: "Weekend shift" },
];

const isPositiveNumberString = (value: string) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0;
};

// ─── Generic Sub-Form ───────────────────────────────────────────────────────

const genericFormSchema = z.object({
  name: z.string().min(1, "Required"),
  header: z.string(),
  subHeader: z.string(),
  buttonText: z.string(),
});

type GenericFormData = z.infer<typeof genericFormSchema>;

function GenericSignupVariantSubForm({
  initialName,
  initialPayload,
  isEditing,
  isSubmitting,
  onSubmit,
  onTypeChange,
  submitButtonText,
}: {
  initialName: string;
  initialPayload: GenericSignupVariantPayload | null;
  isEditing: boolean;
  isSubmitting: boolean;
  onSubmit: (data: CreateOrUpdateSignupVariantRequest) => void;
  onTypeChange: (newType: "generic" | "jobSpecific", currentName: string) => void;
  submitButtonText: string;
}) {
  const form = useForm<GenericFormData>({
    resolver: zodResolver(genericFormSchema),
    defaultValues: {
      name: initialName,
      header: initialPayload?.header ?? "",
      subHeader: initialPayload?.subHeader ?? "",
      buttonText: initialPayload?.buttonText ?? "",
    },
  });

  const handleSubmit = (data: GenericFormData) => {
    mixpanelUtils.trackFormSubmission("Signup variant submit", MIXPANEL_SCREENS.SIGNUP_VARIANTS);
    onSubmit({
      name: data.name,
      payload: {
        type: "generic",
        header: data.header || undefined,
        subHeader: data.subHeader || undefined,
        buttonText: data.buttonText || undefined,
      },
    });
  };

  return (
    <div className="h-full overflow-y-auto p-6">
      <Form {...form}>
        <form
          className="flex flex-col gap-10"
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          <FormSection
            description="Internal identifying information."
            title="1. Basic Information"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem className="text-slate-700">
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FormSection>

          <FormDivider />

          <FormSection
            description="Choose between a generic signup variant or one linked to a specific job."
            title="2. Variant Type"
          >
            <FormItem className="text-slate-700">
              <FormLabel>Type</FormLabel>
              <Select
                disabled={isEditing}
                onValueChange={(val) => {
                  onTypeChange(val as "generic" | "jobSpecific", form.getValues("name"));
                }}
                value="generic"
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="generic">Generic</SelectItem>
                  <SelectItem value="jobSpecific">Job Specific</SelectItem>
                </SelectContent>
              </Select>
            </FormItem>
          </FormSection>

          <FormDivider />

          <FormSection
            description="Configure dynamic content. Leave blank to use the default content."
            title="3. Variant Payload"
          >
            <FormField
              control={form.control}
              name="header"
              render={({ field }) => (
                <FormItem className="text-slate-700">
                  <FormLabel>Header</FormLabel>
                  <FormControl>
                    <Input placeholder="Header" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="subHeader"
              render={({ field }) => (
                <FormItem className="text-slate-700">
                  <FormLabel>Subheader</FormLabel>
                  <FormControl>
                    <Input placeholder="Subheader" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="buttonText"
              render={({ field }) => (
                <FormItem className="text-slate-700">
                  <FormLabel>Button Text</FormLabel>
                  <FormControl>
                    <Input placeholder="Button text" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FormSection>

          <Row className="justify-end">
            <Button disabled={isSubmitting} type="submit">
              {submitButtonText}
            </Button>
          </Row>
        </form>
      </Form>
    </div>
  );
}

// ─── Job-Specific Sub-Form ─────────────────────────────────────────────────

const screeningQuestionFieldSchema = z.object({
  id: z.number(),
  question: z.string(),
  required: z.boolean(),
  type: z.nativeEnum(ScreeningQuestionType).optional(),
  options: z.array(z.string()).optional(),
});

const jobSpecificFormSchema = z
  .object({
    name: z.string().min(1, "Required"),
    companyId: z.string().min(1, "Required"),
    jobId: z.string().min(1, "Required"),
    talentId: z.string().min(1, "Required"),
    desiredPosition: z.string().min(1, "Required"),
    heroImageUrl: z.string().min(1, "Required"),
    location: z.string().min(1, "Required"),
    jobTitle: z.string().min(1, "Required"),
    industry: z.string().min(1, "Required"),
    compensationMin: z.string().min(1, "Required"),
    compensationMax: z.string().min(1, "Required"),
    compensationType: z.nativeEnum(CompensationType),
    badges: z.array(z.string()),
    shifts: z.array(z.string()),
    theWork: z.string().min(1, "Required"),
    relocationAvailable: z.boolean(),
    relocationMin: z.string(),
    relocationMax: z.string(),
    whatWereLookingFor: z.string().min(1, "Required"),
    whyThisShop: z.string().min(1, "Required"),
    ctaLabel: z.string(),
    buttonText: z.string(),
    screeningQuestions: z.array(screeningQuestionFieldSchema),
  })
  .superRefine((data, ctx) => {
    const hasValidCompMin = isPositiveNumberString(data.compensationMin);
    const hasValidCompMax = isPositiveNumberString(data.compensationMax);

    if (!hasValidCompMin) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Must be greater than 0",
        path: ["compensationMin"],
      });
    }
    if (!hasValidCompMax) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Must be greater than 0",
        path: ["compensationMax"],
      });
    }
    if (
      hasValidCompMin &&
      hasValidCompMax &&
      Number(data.compensationMax) < Number(data.compensationMin)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Must be greater than or equal to min",
        path: ["compensationMax"],
      });
    }

    if (!data.relocationAvailable) return;

    const hasValidReloMin = isPositiveNumberString(data.relocationMin);
    const hasValidReloMax = isPositiveNumberString(data.relocationMax);

    if (!hasValidReloMin) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Must be greater than 0",
        path: ["relocationMin"],
      });
    }
    if (!hasValidReloMax) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Must be greater than 0",
        path: ["relocationMax"],
      });
    }
    if (
      hasValidReloMin &&
      hasValidReloMax &&
      Number(data.relocationMax) < Number(data.relocationMin)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Must be greater than or equal to min",
        path: ["relocationMax"],
      });
    }
  });

type JobSpecificFormData = z.infer<typeof jobSpecificFormSchema>;

function JobSpecificSignupVariantSubForm({
  initialName,
  initialPayload,
  isEditing,
  isSubmitting,
  onSubmit,
  onTypeChange,
  submitButtonText,
}: {
  initialName: string;
  initialPayload: JobSpecificSignupVariantPayload | null;
  isEditing: boolean;
  isSubmitting: boolean;
  onSubmit: (data: CreateOrUpdateSignupVariantRequest) => void;
  onTypeChange: (newType: "generic" | "jobSpecific", currentName: string) => void;
  submitButtonText: string;
}) {
  const companies = useGetCachedCompaniesSummary();
  const companiesWithJobs = useMemo(
    () => companies.filter(({ jobs }) => jobs.length > 0),
    [companies],
  );

  const initialCompanyId = useMemo(() => {
    if (!initialPayload?.jobId) return "";
    const company = companiesWithJobs.find(({ jobs }) =>
      jobs.some((job) => job.id === initialPayload.jobId),
    );
    return company?.id ?? "";
  }, [initialPayload, companiesWithJobs]);

  const form = useForm<JobSpecificFormData>({
    resolver: zodResolver(jobSpecificFormSchema),
    defaultValues: {
      name: initialName,
      companyId: initialCompanyId,
      jobId: initialPayload?.jobId ?? "",
      talentId: initialPayload?.talentId ?? "",
      desiredPosition: initialPayload?.desiredPosition ?? "",
      heroImageUrl: initialPayload?.heroImageUrl ?? "",
      location: initialPayload?.location ?? "",
      jobTitle: initialPayload?.jobTitle ?? "",
      industry: initialPayload?.industry ?? "",
      compensationMin: initialPayload?.compensationMin.toString() ?? "",
      compensationMax: initialPayload?.compensationMax.toString() ?? "",
      compensationType: initialPayload?.compensationType ?? CompensationType.HOURLY,
      badges: initialPayload?.badges ?? [],
      shifts: initialPayload?.shifts ?? [],
      theWork: initialPayload?.theWork ?? "",
      relocationAvailable: initialPayload?.relocationAvailable ?? false,
      relocationMin: initialPayload?.relocationAvailable
        ? initialPayload.relocationMin.toString()
        : "",
      relocationMax: initialPayload?.relocationAvailable
        ? initialPayload.relocationMax.toString()
        : "",
      whatWereLookingFor: initialPayload?.whatWereLookingFor ?? "",
      whyThisShop: initialPayload?.whyThisShop ?? "",
      ctaLabel: initialPayload?.ctaLabel ?? "",
      buttonText: initialPayload?.buttonText ?? "",
      screeningQuestions: initialPayload?.screeningQuestions ?? [],
    },
  });

  // When companies load after form init, set the company and job selection.
  useEffect(() => {
    if (!initialCompanyId || form.getValues("companyId")) return;
    form.setValue("companyId", initialCompanyId);
    if (initialPayload?.jobId) {
      form.setValue("jobId", initialPayload.jobId);
    }
    if (initialPayload?.talentId) {
      form.setValue("talentId", initialPayload.talentId);
    }
  }, [initialCompanyId, form, initialPayload]);

  const watched = form.watch();
  const { companyId, jobId, relocationAvailable } = watched;
  const compensationUnitLabel =
    watched.compensationType === CompensationType.YEARLY ? "$/yr" : "$/hr";

  const company = companiesWithJobs.find(({ id }) => id === companyId);
  const jobs = company?.jobs.filter((job) => job.status !== JobStatus.CLOSED) ?? [];
  const companyOptions = companiesWithJobs.map((c) => ({ value: c.id, label: c.name }));
  const jobOptions = jobs.map((job) => ({ value: job.id, label: job.title }));

  const { data: selectedJob } = useGetTargetJob({ companyId, jobId });

  useEffect(() => {
    const currentJobId = form.getValues("jobId");
    const nextTalentId =
      selectedJob && selectedJob.id === currentJobId
        ? selectedJob.gcpTalentId
        : "";
    if (form.getValues("talentId") === nextTalentId) return;
    form.setValue("talentId", nextTalentId);
  }, [form, selectedJob]);

  const jobScreeningQuestions = useMemo(() => {
    const sq = selectedJob?.screeningQuestions?.screeningQuestions;
    if (!sq || sq.length === 0) return [];
    return sq.map((q) => ({
      id: q.id,
      question: q.question,
      required: q.required,
      type: q.type,
      options: q.options,
    }));
  }, [selectedJob]);

  const handleHeroImageSelect = async (file: File) => {
    const base64 = await misc.convertFileToBase64(file);
    if (typeof base64 !== "string") return;
    form.setValue("heroImageUrl", base64);
  };

  const handleSubmit = (data: JobSpecificFormData) => {
    mixpanelUtils.trackFormSubmission("Signup variant submit", MIXPANEL_SCREENS.SIGNUP_VARIANTS);
    const relocationPayload = data.relocationAvailable
      ? {
        relocationAvailable: true as const,
        relocationMin: Number(data.relocationMin),
        relocationMax: Number(data.relocationMax),
      }
      : { relocationAvailable: false as const };

    onSubmit({
      name: data.name,
      payload: {
        type: "jobSpecific",
        jobId: data.jobId,
        desiredPosition: data.desiredPosition,
        heroImageUrl: data.heroImageUrl,
        location: data.location,
        jobTitle: data.jobTitle,
        industry: data.industry,
        talentId: data.talentId,
        compensationMin: Number(data.compensationMin),
        compensationMax: Number(data.compensationMax),
        compensationType: data.compensationType,
        badges: data.badges as SignupVariantBadge[],
        shifts: data.shifts as ShiftType[],
        theWork: data.theWork,
        ...relocationPayload,
        whatWereLookingFor: data.whatWereLookingFor,
        whyThisShop: data.whyThisShop,
        ctaLabel: data.ctaLabel || undefined,
        buttonText: data.buttonText || undefined,
        screeningQuestions: data.screeningQuestions,
      },
    });
  };

  return (
    <Row className="h-full">
      <div className="min-w-0 flex-1 overflow-y-auto p-6">
        <Form {...form}>
          <form
            className="flex flex-col gap-10"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <FormSection
              description="Internal identifying information."
              title="1. Basic Information"
            >
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="text-slate-700">
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </FormSection>

            <FormDivider />

            <FormSection
              description="Choose between a generic signup variant or one linked to a specific job."
              title="2. Variant Type"
            >
              <FormItem className="text-slate-700">
                <FormLabel>Type</FormLabel>
                <Select
                  disabled={isEditing}
                  value="jobSpecific"
                  onValueChange={(val) => {
                    onTypeChange(
                      val as "generic" | "jobSpecific",
                      form.getValues("name"),
                    );
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="generic">Generic</SelectItem>
                    <SelectItem value="jobSpecific">Job Specific</SelectItem>
                  </SelectContent>
                </Select>
              </FormItem>
            </FormSection>

            <FormDivider />

            <FormSection
              description="Link this variant to a specific job."
              title="3. Job Association"
            >
              <FormField
                control={form.control}
                name="companyId"
                render={({ field }) => (
                  <FormItem className="text-slate-700">
                    <FormLabel>Company</FormLabel>
                    <Select
                      disabled={isEditing}
                      value={field.value}
                      onValueChange={(val) => {
                        field.onChange(val);
                        form.setValue("jobId", "");
                        form.setValue("talentId", "");
                      }}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a company" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {companyOptions.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="jobId"
                render={({ field }) => (
                  <FormItem className="text-slate-700">
                    <FormLabel>Job</FormLabel>
                    <Select disabled={isEditing} value={field.value} onValueChange={(val) => {
                      field.onChange(val);
                    }}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a job" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {jobOptions.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </FormSection>

            <FormDivider />

            <FormSection
              description="Configure dynamic content. Leave blank to use the default content."
              title="4. Variant Payload"
            >
              <FormField
                control={form.control}
                name="desiredPosition"
                render={({ field }) => (
                  <FormItem className="text-slate-700">
                    <FormLabel>Desired Position</FormLabel>
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a desired position" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {desiredPositionOptions.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="heroImageUrl"
                render={() => (
                  <FormItem className="text-slate-700">
                    <FormLabel>Hero Image</FormLabel>
                    <FormControl>
                      <Input
                        accept=".png,.jpg,.jpeg,.webp"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) void handleHeroImageSelect(file);
                        }}
                        type="file"
                      />
                    </FormControl>
                    {watched.heroImageUrl && (
                      <img
                        alt="Hero preview"
                        className="h-24 rounded-md border object-cover"
                        src={watched.heroImageUrl}
                      />
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="location"
                render={({ field }) => (
                  <FormItem className="text-slate-700">
                    <FormLabel>Location</FormLabel>
                    <FormControl>
                      <Input placeholder="Location" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="jobTitle"
                render={({ field }) => (
                  <FormItem className="text-slate-700">
                    <FormLabel>Job Title</FormLabel>
                    <FormControl>
                      <Input placeholder="Job Title" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="industry"
                render={({ field }) => (
                  <FormItem className="text-slate-700">
                    <FormLabel>Industry</FormLabel>
                    <FormControl>
                      <Input placeholder="Industry" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="compensationType"
                render={({ field }) => (
                  <FormItem className="text-slate-700">
                    <FormLabel>Compensation Type</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select compensation type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {compensationTypeOptions.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Row className="gap-4">
                <FormField
                  control={form.control}
                  name="compensationMin"
                  render={({ field }) => (
                    <FormItem className="flex-1 text-slate-700">
                      <FormLabel>{`Compensation Min (${compensationUnitLabel})`}</FormLabel>
                      <FormControl>
                        <Input min={1} placeholder="Compensation Min" type="number" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="compensationMax"
                  render={({ field }) => (
                    <FormItem className="flex-1 text-slate-700">
                      <FormLabel>{`Compensation Max (${compensationUnitLabel})`}</FormLabel>
                      <FormControl>
                        <Input min={1} placeholder="Compensation Max" type="number" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </Row>

              <FormField
                control={form.control}
                name="badges"
                render={({ field }) => (
                  <FormItem className="text-slate-700">
                    <FormLabel>Badges</FormLabel>
                    <Column className="gap-2">
                      {BADGE_OPTIONS.map((option) => (
                        <Row className="items-center gap-2" key={option.value}>
                          <Checkbox
                            id={`badge-${option.value}`}
                            checked={field.value.includes(option.value)}
                            onCheckedChange={(checked) => {
                              const next = checked
                                ? [...field.value, option.value]
                                : field.value.filter((v) => v !== (option.value as string));
                              field.onChange(next);
                            }}
                          />
                          <Label className="font-normal" htmlFor={`badge-${option.value}`}>
                            {option.label}
                          </Label>
                        </Row>
                      ))}
                    </Column>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="shifts"
                render={({ field }) => (
                  <FormItem className="text-slate-700">
                    <FormLabel>Shifts</FormLabel>
                    <Column className="gap-2">
                      {SHIFT_OPTIONS.map((option) => (
                        <Row className="items-center gap-2" key={option.value}>
                          <Checkbox
                            id={`shift-${option.value}`}
                            checked={field.value.includes(option.value)}
                            onCheckedChange={(checked) => {
                              const next = checked
                                ? [...field.value, option.value]
                                : field.value.filter((v) => v !== (option.value as string));
                              field.onChange(next);
                            }}
                          />
                          <Label className="font-normal" htmlFor={`shift-${option.value}`}>
                            {option.label}
                          </Label>
                        </Row>
                      ))}
                    </Column>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="theWork"
                render={({ field }) => (
                  <FormItem className="text-slate-700">
                    <FormLabel>The Work</FormLabel>
                    <FormControl>
                      <Textarea placeholder="Describe the work..." rows={4} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="relocationAvailable"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-3 text-slate-700">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={(checked) => {
                          field.onChange(checked);
                          if (!checked) {
                            form.setValue("relocationMin", "");
                            form.setValue("relocationMax", "");
                          }
                        }}
                      />
                    </FormControl>
                    <FormLabel className="!mt-0">Relocation available</FormLabel>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Row className={relocationAvailable ? "gap-4" : "hidden"}>
                <FormField
                  control={form.control}
                  name="relocationMin"
                  render={({ field }) => (
                    <FormItem className="flex-1 text-slate-700">
                      <FormLabel>Relocation Min ($)</FormLabel>
                      <FormControl>
                        <Input min={1} placeholder="Relocation Min" type="number" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="relocationMax"
                  render={({ field }) => (
                    <FormItem className="flex-1 text-slate-700">
                      <FormLabel>Relocation Max ($)</FormLabel>
                      <FormControl>
                        <Input min={1} placeholder="Relocation Max" type="number" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </Row>

              <FormField
                control={form.control}
                name="whatWereLookingFor"
                render={({ field }) => (
                  <FormItem className="text-slate-700">
                    <FormLabel>What We&apos;re Looking For</FormLabel>
                    <FormControl>
                      <Textarea
                        rows={4}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="whyThisShop"
                render={({ field }) => (
                  <FormItem className="text-slate-700">
                    <FormLabel>Why this Shop</FormLabel>
                    <FormControl>
                      <Textarea
                        rows={4}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="ctaLabel"
                render={({ field }) => (
                  <FormItem className="text-slate-700">
                    <FormLabel>CTA Label</FormLabel>
                    <FormControl>
                      <Input placeholder="CTA Label" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="buttonText"
                render={({ field }) => (
                  <FormItem className="text-slate-700">
                    <FormLabel>Button Text</FormLabel>
                    <FormControl>
                      <Input placeholder="Button Text" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="screeningQuestions"
                render={({ field }) => (
                  <FormItem className="text-slate-700">
                    <FormLabel>Screening Questions</FormLabel>
                    {jobScreeningQuestions.length > 0 ? (
                      <Column className="gap-2">
                        {jobScreeningQuestions.map((q) => {
                          const isSelected = field.value.some(
                            (sq) => sq.id === q.id,
                          );
                          return (
                            <Row className="items-start gap-2" key={q.id}>
                              <Checkbox
                                id={`screening-${String(q.id)}`}
                                checked={isSelected}
                                onCheckedChange={(checked) => {
                                  const next = checked
                                    ? [...field.value, q]
                                    : field.value.filter(
                                      (sq) => sq.id !== q.id,
                                    );
                                  field.onChange(next);
                                }}
                              />
                              <Label className="font-normal" htmlFor={`screening-${String(q.id)}`}>
                                {q.question}
                                {q.required && (
                                  <span className="text-red-500">
                                    *
                                  </span>
                                )}
                              </Label>
                            </Row>
                          );
                        })}
                      </Column>
                    ) : (
                      <p className="text-sm text-slate-400">
                        {jobId
                          ? "No screening questions found for this job."
                          : "Select a job to see available screening questions."}
                      </p>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
            </FormSection>

            <Row className="justify-end">
              <Button disabled={isSubmitting} type="submit">
                {submitButtonText}
              </Button>
            </Row>
          </form>
        </Form>
      </div>

      <Row className="items-center justify-center border-l border-slate-200 p-8">
        <div className="rounded-2xl bg-slate-100 p-8">
          <JobSignupPreview
            badges={watched.badges}
            buttonText={watched.buttonText}
            compensationMax={watched.compensationMax}
            compensationMin={watched.compensationMin}
            compensationType={watched.compensationType}
            ctaLabel={watched.ctaLabel}
            header={watched.jobTitle}
            heroImageUrl={watched.heroImageUrl}
            location={watched.location}
            relocationAvailable={watched.relocationAvailable}
            relocationMax={watched.relocationMax}
            relocationMin={watched.relocationMin}
            shifts={watched.shifts}
            subHeader={watched.industry}
            theWork={watched.theWork}
            whatWereLookingFor={watched.whatWereLookingFor}
            whyThisShop={watched.whyThisShop}
          />
        </div>
      </Row>
    </Row>
  );
}

// ─── Outer Component ─────────────────────────────────────────────────────

export const SignupVariantsForm = ({
  isSubmitting,
  onSubmit,
  signupVariant,
  submitButtonText,
}: {
  isSubmitting: boolean;
  onSubmit: (data: CreateOrUpdateSignupVariantRequest) => void;
  signupVariant?: SignupVariant;
  submitButtonText: string;
}) => {
  const payload = signupVariant?.payload as unknown as
    | SignupVariantPayload
    | null
    | undefined;

  const [savedName, setSavedName] = useState<string>(signupVariant?.name ?? "");
  const [variantType, setVariantType] = useState<"generic" | "jobSpecific">(
    payload?.type ?? "generic",
  );

  const handleTypeChange = (
    newType: "generic" | "jobSpecific",
    currentName: string,
  ) => {
    setSavedName(currentName);
    setVariantType(newType);
  };

  const isEditing = !!signupVariant;

  if (variantType === "jobSpecific") {
    return (
      <JobSpecificSignupVariantSubForm
        initialName={savedName}
        initialPayload={payload?.type === "jobSpecific" ? payload : null}
        isEditing={isEditing}
        isSubmitting={isSubmitting}
        key="jobSpecific"
        onSubmit={onSubmit}
        onTypeChange={handleTypeChange}
        submitButtonText={submitButtonText}
      />
    );
  }

  return (
    <GenericSignupVariantSubForm
      initialName={savedName}
      initialPayload={payload?.type === "generic" ? payload : null}
      isEditing={isEditing}
      isSubmitting={isSubmitting}
      key="generic"
      onSubmit={onSubmit}
      onTypeChange={handleTypeChange}
      submitButtonText={submitButtonText}
    />
  );
};
